import { type BottleUnit, fromOz, type Tracked } from "@babia/checks";
import type { DayTotals } from "../../lib/overview.ts";
import { shortDuration, weekdayDay } from "../../lib/time.ts";

/**
 * Every charted number as a table, newest day first: the charts' plain-text
 * twin, bottles in the household's unit. Changes and sleep only when the
 * family tracks them.
 */
export function DayTable({
  days,
  tracking,
  unit,
}: {
  days: DayTotals[];
  tracking: Tracked;
  unit: BottleUnit;
}) {
  const columns: { label: string; value: (d: DayTotals) => string | number }[] = [
    { label: unit, value: (d) => fromOz(d.oz, unit) },
    { label: "Bottles", value: (d) => d.bottles },
    ...(tracking.diaper
      ? [
          { label: "Changes", value: (d: DayTotals) => d.changes },
          { label: "Dirty", value: (d: DayTotals) => d.dirty },
        ]
      : []),
    ...(tracking.sleep
      ? [
          {
            label: "Sleep",
            value: (d: DayTotals) => (d.sleepMs ? shortDuration(d.sleepMs) : "—"),
          },
        ]
      : []),
  ];
  return (
    <table className="w-full border-collapse text-left text-[13px] tabular-nums">
      <thead>
        <tr className="border-b border-ink text-[11px] uppercase tracking-[0.06em]">
          <th className="py-1.5 pr-2 font-normal opacity-60">Day</th>
          {columns.map((c) => (
            <th key={c.label} className="py-1.5 pr-2 text-right font-normal opacity-60 last:pr-0">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...days].reverse().map((d) => (
          <tr key={d.day.getTime()} className="border-t border-rule">
            <th scope="row" className="py-1.5 pr-2 font-normal">
              {d.isToday ? "Today" : weekdayDay(d.day)}
            </th>
            {columns.map((c) => (
              <td key={c.label} className="py-1.5 pr-2 text-right last:pr-0">
                {c.value(d)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
